import { landmineNames } from '@mm/constants/landmine-names';
import { useFeatureFlagsCRUDActions } from '@mm/state/actions/FeatureFlagsCRUDActions';
import usersCRUDActions, {
  useUsersCRUDActions,
} from '@mm/state/actions/UsersCRUDActions';
import modalActions, {
  useModalActions,
} from '@mm/state/modules/modalsState/actions';
import {
  selectActiveFacebookPage,
  selectActiveFacebookPageFromSession,
} from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { ReactComponent as MegaphoneIcon } from 'assets/icons/megaphone.svg';
import customersLogo from 'assets/logo-customers-blue.svg';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { getDeviceInfo } from '../use-device-info';
import './use-desktop-notifications.pcss';

type BrowserPermissionStatus = 'granted' | 'denied' | 'default';

export const useDesktopNotifications = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();
  const session = useSelector((state: StoreState) => state.session.session);
  const activeCompany = useSelector(activeCompanySelector);
  const { isMobile } = getDeviceInfo();
  const { updateUserAttributes } = useUsersCRUDActions();
  const facebookPageFromSession = useSelector(
    selectActiveFacebookPageFromSession,
  );
  const activeFacebookPage = useSelector(selectActiveFacebookPage);
  const featureFlagsCRUDActions = useFeatureFlagsCRUDActions();
  const fbPageId = activeFacebookPage?.id || facebookPageFromSession?.id;
  const featureModalActions = useModalActions();
  const featureFlags = useSelector(combinedFeatureFlagsSelector);

  const [notificationServiceWorkerAdded, setNotificationServiceWorkerAdded] =
    useState(false);

  const [
    browserNotificationPermissionsStatus,
    setBrowserNotificationPermissionsStatus,
  ] = useState<BrowserPermissionStatus | undefined>(undefined);

  const [isUnsubscribedFromNotifications, setIsUnsubscribedFromNotifications] =
    useState(true);

  const [oneSignalPlayerId, setOneSignalPlayerId] = useState<string | null>(
    null,
  );

  const safelyGetSubscriptionState = async (): Promise<
    [boolean, boolean, BrowserPermissionStatus, string | null]
  > => {
    try {
      const res = await Promise.all([
        OneSignal.isPushNotificationsEnabled(),
        OneSignal.isOptedOut(),
        OneSignal.getNotificationPermission(),
        OneSignal.getUserId(),
      ]);
      return res;
    } catch (e) {
      // Note: These default might not be currect for 100% of cases,
      // but still better than undefined
      // Note 2: this is a temporary debug output to test in Prod
      console.error('Error getting notifications subscription state: ', e);
      return [false, true, 'default', null];
    }
  };

  const getNotificationsSubscriptionState = async () => {
    const [
      isPushEnabled,
      isOptedOut,
      browserPermissionStatus,
      freshOneSignalPlayerId,
    ] = await safelyGetSubscriptionState();

    if (notificationServiceWorkerAdded !== isPushEnabled) {
      setNotificationServiceWorkerAdded(isPushEnabled);
    }
    if (browserNotificationPermissionsStatus !== browserPermissionStatus) {
      setBrowserNotificationPermissionsStatus(browserPermissionStatus);
    }
    if (oneSignalPlayerId !== freshOneSignalPlayerId) {
      setOneSignalPlayerId(freshOneSignalPlayerId);
    }
    if (isUnsubscribedFromNotifications !== isOptedOut) {
      setIsUnsubscribedFromNotifications(isOptedOut);
    }
    return {
      isPushEnabled,
      isOptedOut,
      freshOneSignalPlayerId,
    };
  };

  useEffect(() => {
    if (fbPageId) {
      featureFlagsCRUDActions.retrieveSubscriptionFeatures(fbPageId);
      featureFlagsCRUDActions.retrieveFeaturesForPlans(fbPageId);
    }
  }, [fbPageId]);

  useEffect(() => {
    getNotificationsSubscriptionState();
  }, []);

  const changeOneSignalSubscriptionStatus = async (
    subscribe: boolean,
    isPushEnabled: boolean,
  ) => {
    if (subscribe) {
      //  displayNativePushNotificationPopup

      if (!isPushEnabled) {
        // await safelyRequestPermission
        try {
          await OneSignal.showNativePrompt();
        } catch (e) {
          console.error('Error requesting OneSignal native prompt: ', e);
          if (
            e.status ===
            'The Notification permission may only be requested from inside a short running user-generated event handler.'
          ) {
            console.error('Trying to request Notification permission');
            Notification?.requestPermission();
          }
        }
        await getNotificationsSubscriptionState();
      }
      // Note: is available only after push is enabled via native prompt
      try {
        await OneSignal.setSubscription(true);
      } catch (e) {
        console.error('Error setting notifications subscription state: ', e);
      }
    }
    const { isOptedOut } = await getNotificationsSubscriptionState();
    if (!subscribe && !isOptedOut) {
      await OneSignal.setSubscription(false);
    }
    const { freshOneSignalPlayerId } =
      await getNotificationsSubscriptionState();

    const { desktopNotifications } = session.userAttributes;
    if (
      activeCompany &&
      desktopNotifications &&
      freshOneSignalPlayerId !== null
    ) {
      const thisDeviceSaved = desktopNotifications.desktopDevices.includes(
        freshOneSignalPlayerId,
      );
      const updatedDevices = thisDeviceSaved
        ? desktopNotifications.desktopDevices
        : [freshOneSignalPlayerId, ...desktopNotifications.desktopDevices];
      if (!thisDeviceSaved) {
        dispatch(
          usersCRUDActions.updateUserAttributes(activeCompany.id, session.id, {
            desktopNotifications: {
              ...desktopNotifications,
              desktopDevices: updatedDevices,
            },
          }),
        );
      }
    }
  };

  const openDesktopNotificationsModal = useCallback(
    (onCloseHandler?: () => void) => {
      if (
        !isMobile &&
        (!notificationServiceWorkerAdded || isUnsubscribedFromNotifications) &&
        !session.userAttributes?.showPaywall
      ) {
        dispatch(
          modalActions.openGeneralConfirmModal({
            headerText: '',
            messageText: (
              <div className="desktop-notifications-modal__content">
                <div>
                  <div>
                    <img
                      src={customersLogo}
                      alt="MobileMonkey"
                      className="desktop-notifications-modal__mm-logo"
                    />
                  </div>
                  <div className="desktop-notifications-modal__megaphon-container">
                    <MegaphoneIcon />
                  </div>
                </div>
                <div className="desktop-notifications-modal__text">
                  Would you like to be notified on this browser for updates in
                  your inbox?
                </div>
              </div>
            ),
            okButtonText: 'YES',
            okButtonStyling: { blue: true },
            cancelButtonFirst: true,
            cancelButtonText: 'NO THANKS',

            onOpenHandler: () => {
              if (activeCompany) {
                updateUserAttributes(activeCompany.id, session.id, {
                  desktopNotifications: session.userAttributes
                    .desktopNotifications
                    ? {
                        ...session.userAttributes.desktopNotifications,
                        seenDesktopNotificationsPromoPopup: true,
                      }
                    : {
                        seenDesktopNotificationsPromoPopup: true,
                        needsFollowUp: true,
                        widgetTriggered: true,
                        assignedToMe: true,
                        allMessages: true,
                        unicornLead: true,
                        desktopDevices: [],
                        needsFollowUpUnselectedPages: {},
                        widgetTriggeredUnselectedPages: {},
                        allMessagesUnselectedPages: {},
                        assignedToMeUnselectedPages: {},
                        unicornLeadUnselectedPages: {},
                      },
                });
              }
            },
            onCloseHandler: async (subscribe: boolean) => {
              if (!featureFlags?.desktopNotifications && subscribe) {
                return featureModalActions.openUniversalUpgradeLandmineModal({
                  landmineName:
                    landmineNames['MY_ACCOUNT__ENABLE-DESKTOP-NOTIFICATIONS'],
                  feature: 'desktopNotifications',
                });
              }

              const { isPushEnabled } =
                await getNotificationsSubscriptionState();
              changeOneSignalSubscriptionStatus(subscribe, isPushEnabled);
              if (isPushEnabled && onCloseHandler) {
                onCloseHandler();
              }
            },
          }),
        );
      }
    },
    [
      activeCompany,
      featureFlags,
      session,
      session.userAttributes,
      notificationServiceWorkerAdded,
      isUnsubscribedFromNotifications,
      isMobile,
    ],
  );

  return {
    openDesktopNotificationsModal,
    notificationServiceWorkerAdded,
    browserNotificationPermissionsStatus,
    oneSignalPlayerId,
    isUnsubscribedFromNotifications,
  };
};
